import React from "react";
import Chart from "react-apexcharts";

const palette = ["#0f172a", "#f97316", "#14b8a6", "#38bdf8", "#16a34a", "#ef4444", "#eab308"];

const baseCardClassName = "rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm";

const chartBase = {
  chart: {
    toolbar: { show: false },
    zoom: { enabled: false },
    fontFamily: "inherit",
  },
  noData: {
    text: "No data",
    align: "center",
    verticalAlign: "middle",
    style: {
      color: "#64748b",
      fontSize: "14px",
    },
  },
  dataLabels: { enabled: false },
  stroke: { curve: "smooth", width: 3 },
  grid: { borderColor: "#e2e8f0", strokeDashArray: 4 },
  legend: { position: "top", fontSize: "13px" },
  colors: palette,
};

const getLabels = (items) => items.map((item) => String(item?.label ?? "No Data"));
const getValues = (items) => items.map((item) => Number(item?.value ?? 0));
const safeAxisNumber = (value) => String(Math.round(Number(value ?? 0)));

const ensureCategoryDataset = (items, fallbackLabel = "No Data") => {
  if (!Array.isArray(items) || !items.length) {
    return {
      labels: [fallbackLabel],
      values: [0],
    };
  }

  const labels = getLabels(items);
  const values = getValues(items);

  return {
    labels: labels.length ? labels : [fallbackLabel],
    values: values.length ? values : [0],
  };
};

const ensureTrendDataset = (items, valueKey = "value", fallbackLabel = "No Data") => {
  if (!Array.isArray(items) || !items.length) {
    return {
      labels: [fallbackLabel],
      values: [0],
    };
  }

  return {
    labels: items.map((item) => String(item?.label ?? fallbackLabel)),
    values: items.map((item) => Number(item?.[valueKey] ?? item?.value ?? 0)),
  };
};

function ChartCard({ title, description, type, series, options, height = 320 }) {
  return (
    <div className={baseCardClassName}>
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
        <p className="mt-1 text-sm text-slate-500">{description}</p>
      </div>
      <Chart type={type} series={series} options={options} height={height} />
    </div>
  );
}

export default function AnalyticsChartsGrid({ analytics }) {
  const statusDataset = ensureCategoryDataset(analytics.statusBreakdown);
  const ticketTrendDataset = ensureTrendDataset(analytics.ticketTrend, "value");
  const taskTrendDataset = ensureTrendDataset(analytics.taskTrend, "value");
  const customerTicketDataset = ensureCategoryDataset(analytics.ticketsByCustomer);
  const customerTaskDataset = ensureCategoryDataset(analytics.tasksByCustomer);
  const organizationDataset = ensureCategoryDataset(analytics.ticketsByOrganization);

  const ticketTrendSeries = [
    {
      name: "Tickets",
      data: ticketTrendDataset.values,
    },
  ];

  const taskTrendSeries = [
    {
      name: "Tasks",
      data: taskTrendDataset.values,
    },
  ];

  const customerTicketSeries = [
    {
      name: "Tickets",
      data: customerTicketDataset.values,
    },
  ];

  const customerTaskSeries = [
    {
      name: "Tasks",
      data: customerTaskDataset.values,
    },
  ];

  const organizationSeries = [
    {
      name: "Tickets",
      data: organizationDataset.values,
    },
  ];

  return (
    <div className="grid gap-4 xl:grid-cols-12">
      <div className="xl:col-span-4">
        <ChartCard
          title="Customer Activity Summary"
          description="Pie chart of ticket statuses across all selected organizations, customers, and customer users."
          type="pie"
          height={300}
          series={statusDataset.values}
          options={{
            ...chartBase,
            labels: statusDataset.labels,
            stroke: { width: 1, colors: ["#fff"] },
            legend: { position: "bottom" },
          }}
        />
      </div>

      <div className="xl:col-span-8">
        <ChartCard
          title="Customer User Activity Tracking"
          description="Line chart showing how customer tickets trend over time in the current filter window."
          type="line"
          series={ticketTrendSeries}
          options={{
            ...chartBase,
            xaxis: { categories: ticketTrendDataset.labels, labels: { rotate: -35 } },
            yaxis: { labels: { formatter: safeAxisNumber } },
          }}
        />
      </div>

      <div className="xl:col-span-6">
        <ChartCard
          title="Tasks Generated From Customer Tickets"
          description="Area chart of operational workload created by customer support requests."
          type="area"
          series={taskTrendSeries}
          options={{
            ...chartBase,
            fill: {
              type: "gradient",
              gradient: {
                shadeIntensity: 1,
                opacityFrom: 0.35,
                opacityTo: 0.05,
                stops: [0, 90, 100],
              },
            },
            xaxis: { categories: taskTrendDataset.labels, labels: { rotate: -35 } },
            yaxis: { labels: { formatter: safeAxisNumber } },
          }}
        />
      </div>

      <div className="xl:col-span-6">
        <ChartCard
          title="Total Tickets Created Per Customer"
          description="Bar chart to compare customer issue volume and identify the biggest ticket-generating accounts."
          type="bar"
          series={customerTicketSeries}
          options={{
            ...chartBase,
            plotOptions: { bar: { borderRadius: 8, columnWidth: "52%" } },
            xaxis: { categories: customerTicketDataset.labels, labels: { rotate: -30, trim: true } },
            yaxis: { labels: { formatter: safeAxisNumber } },
          }}
        />
      </div>

      <div className="xl:col-span-6">
        <ChartCard
          title="Total Tasks Generated Per Customer"
          description="Bar chart for customers that create the most internal operational work from their tickets."
          type="bar"
          series={customerTaskSeries}
          options={{
            ...chartBase,
            colors: ["#14b8a6"],
            plotOptions: { bar: { borderRadius: 8, columnWidth: "52%" } },
            xaxis: { categories: customerTaskDataset.labels, labels: { rotate: -30, trim: true } },
            yaxis: { labels: { formatter: safeAxisNumber } },
          }}
        />
      </div>

      <div className="xl:col-span-6">
        <ChartCard
          title="Organization Reporting View"
          description="Ticket totals by organization to see which client companies are generating the most support demand."
          type="bar"
          series={organizationSeries}
          options={{
            ...chartBase,
            colors: ["#f97316"],
            plotOptions: { bar: { horizontal: true, borderRadius: 8, barHeight: "58%" } },
            xaxis: { categories: organizationDataset.labels },
          }}
        />
      </div>
    </div>
  );
}
